import { referentiel as r } from '@admin-pmvb/core'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { api, corps, gere, lire, requeteMoi, requetes } from '../api'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { Ligne, Section, Vide } from '../composants/Section'
import { LIBELLES_NIVEAU_ROLE } from '../libelles'
import { EntetePage } from '../composants/Ui'
import { OngletsReferentiel } from '../composants/OngletsReferentiel'

const CANAUX = [
  { valeur: 'aucun', libelle: 'Aucun' },
  { valeur: 'email', libelle: 'E-mail' },
  { valeur: 'slack', libelle: 'Slack' },
]

const champsModification: Champ[] = [
  { nom: 'nom', libelle: 'Nom', type: 'texte', requis: true },
  { nom: 'email', libelle: 'Adresse de connexion', type: 'email', requis: true },
  {
    nom: 'canalNotification',
    libelle: 'Notifications',
    type: 'choix',
    requis: true,
    options: CANAUX,
  },
  { nom: 'slackId', libelle: 'Identifiant Slack', type: 'texte' },
]

export function Personnes() {
  const moi = useSuspenseQuery(requeteMoi).data
  const client = useQueryClient()
  const personnes = useQuery(requetes.personnes)
  const roles = useQuery(requetes.roles())
  const entites = useQuery(requetes.entites)
  const rafraichir = () => client.invalidateQueries()
  const admin = moi.administrateur
  const entitesGerees = (entites.data ?? []).filter((e) => gere(moi, e.id) && !e.archivee)
  const champsCreation: Champ[] = [
    ...champsModification,
    {
      nom: 'entiteId',
      libelle: 'Associé de',
      type: 'choix',
      requis: !admin,
      aide: admin ? 'Facultatif : donne le rôle d’associé sur cette entité.' : undefined,
      options: entitesGerees.map((e) => ({ valeur: e.id, libelle: e.nom })),
    },
    { nom: 'responsable', libelle: 'Responsable de cette entité', type: 'booleen' },
  ]
  // Modifiable : ce que dit le serveur (`geree`, d'après tous ses rôles, y compris ceux
  // qu'on ne voit pas d'ici : la titulaire d'un espace privé d'autrui ne l'est pas).
  const modifiable = (personneId: string) =>
    (personnes.data ?? []).some((p) => p.id === personneId && p.geree)

  return (
    <main>
      <EntetePage surtitre="Référentiel" titre="Personnes" />
      <OngletsReferentiel actif="personnes" />
      <Section
        titre="Accès à l’application"
        libelleAjout="Ajouter une personne"
        ajout={
          admin || entitesGerees.length > 0
            ? (fermer) => (
                <Formulaire
                  champs={champsCreation}
                  initial={{ canalNotification: 'aucun' }}
                  onAnnuler={fermer}
                  onValider={async (v) => {
                    await lire(api.personnes.$post({ json: corps(r.personneCreation, v) }))
                    await rafraichir()
                    fermer()
                  }}
                />
              )
            : undefined
        }
      >
        {(admin || entitesGerees.length > 0) && (
          <p className="discret">
            Une personne ajoutée ici peut se connecter avec cette adresse Google. Ses droits
            viennent de ses rôles, à donner depuis la fiche de chaque entité.
          </p>
        )}
        {personnes.isPending && <Vide>Chargement…</Vide>}
        {personnes.data?.map((p) => (
          <Ligne
            key={p.id}
            edition={
              modifiable(p.id)
                ? (fermer) => (
                    <Formulaire
                      champs={champsModification}
                      initial={p}
                      onAnnuler={fermer}
                      onValider={async (v) => {
                        await lire(
                          api.personnes[':id'].$patch({
                            param: { id: p.id },
                            json: corps(r.personneModification, v),
                          }),
                        )
                        await rafraichir()
                        fermer()
                      }}
                    />
                  )
                : undefined
            }
          >
            <strong>{p.nom}</strong>
            {p.type === 'assistant' && <span className="badge">Compte technique</span>}
            <span className="discret"> {p.email}</span>
            <div className="discret">
              {(roles.data ?? [])
                .filter((role) => role.personneId === p.id)
                .map(
                  (role) =>
                    `${role.entite} (${LIBELLES_NIVEAU_ROLE[role.niveau].toLowerCase()}${role.responsable ? ', responsable' : ''})`,
                )
                .join(' · ') || 'Aucun rôle'}
            </div>
          </Ligne>
        ))}
      </Section>
    </main>
  )
}
